import { FC, ReactElement, useCallback, useState } from "react";

import DownloadIcon from "@mui/icons-material/Download";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";

import ImageType from "components/ImageType";
import CustomButton from "components/CustomButton";
import Animation3dView from "components/Animation3dView";
import Loader3d from "components/Loader3d";

import { useConnectionSpeed } from "hooks/useConnectionSpeed";
import useModelLoaded from "hooks/useModelLoaded";

import { EXTERNAL_ROUTES } from "config/routes";

import styles from "./styles";

type ItemModelProps = {
  children?: ReactElement | null;
  image: string;
  frontImage?: string;
  backImage?: string;
  animationUrl?: string;
  modelHash?: string;
  imageUnoptimized?: boolean;
  bgColor?: string | null;
};

const ItemModel: FC<ItemModelProps> = ({
  children,
  frontImage,
  backImage,
  image,
  animationUrl,
  modelHash,
  imageUnoptimized = false,
  bgColor,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isItemRotatable = !!(frontImage || image) && !!backImage;
  const [isModelLoaded, setModelLoaded] = useState(false);

  const [show3dView, set3dView] = useState(false);
  const networkSpeed = useConnectionSpeed();

  const is3dModelAvailable = animationUrl || modelHash;
  const shouldLoad3dModel =
    !isMobile || networkSpeed === null || networkSpeed > 5 || show3dView;

  const handleModelLoad = useCallback(() => {
    setModelLoaded(true);
  }, []);

  const handleLoad3dView = () => {
    set3dView(true);
  };

  useModelLoaded(handleModelLoad);

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection="column"
      position="relative"
    >
      <Box display="grid">
        <Box
          sx={[
            (theme) =>
              styles.assetWrapper(theme, {
                isModelLoaded,
                isModelAvailable: !!is3dModelAvailable,
              }),
            { opacity: isModelLoaded ? 0 : 1 },
            { overflow: backImage ? "hidden" : "initial" },
          ]}
        >
          <ImageType
            data={{
              isItemRotatable,
              imageAlt: "Product image",
              itemImageFront: frontImage || image,
              itemImageBack: backImage,
              bgColor,
            }}
            imageUnoptimized={imageUnoptimized}
          />
          {is3dModelAvailable && shouldLoad3dModel && (
            <Box sx={styles.loader}>
              <Loader3d label="Loading 3d model..." />
            </Box>
          )}
        </Box>
        {is3dModelAvailable && shouldLoad3dModel && (
          <Box
            sx={[
              (theme) =>
                styles.assetWrapper(theme, {
                  isModelLoaded,
                  isModelAvailable: !!is3dModelAvailable,
                }),
              {
                opacity: isModelLoaded ? 1 : 0,
                pointerEvents: isModelLoaded ? "auto" : "none",
              },
            ]}
          >
            <Animation3dView
              animationUrl={
                animationUrl ||
                `${EXTERNAL_ROUTES.WEARABLES_3_0_METADATA}${modelHash}`
              }
            />
          </Box>
        )}
      </Box>

      {children}

      {is3dModelAvailable && !shouldLoad3dModel && (
        <CustomButton color="transparent" onClick={handleLoad3dView}>
          <DownloadIcon />
          Load 3D wearables
        </CustomButton>
      )}
    </Box>
  );
};
export default ItemModel;
